import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

import { Button } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import Portal from '../Portal';
import { getCroppedImg } from './utils';
import { OutlineXIcon } from '@tempmee/react-icons';

interface IImageCropperProps {
  src: string;
  onComplete: (croppedImageUrl: string) => void;
  onClose: () => void;
  rightButtonLoading?: boolean;
  leftButtonText?: string;
  leftButtonOnClick?: () => void;
}

interface IPoint {
  x: number;
  y: number;
}

export const ImageCropper = ({
  src,
  onComplete,
  onClose,
  leftButtonOnClick,
  rightButtonLoading = false,
  leftButtonText = 'Cancel',
}: IImageCropperProps) => {
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState<IPoint>({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_croppedArea: Area, _croppedAreaPixels: Area) => {
    setCroppedAreaPixels(_croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    if (!croppedAreaPixels) {
      return;
    }
    const croppedImageUrl = await getCroppedImg(src, croppedAreaPixels);
    onComplete(croppedImageUrl);
  };

  const handleLeftButtonClick = () => {
    if (leftButtonOnClick) {
      leftButtonOnClick();
    } else {
      onClose();
    }
  };

  return (
    <Portal>
      <div
        data-cy="image-cropper"
        className="modal"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            width: 500,
            backgroundColor: colors.shades[0],
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 14,
            padding: 35,
            paddingBottom: 48,
            overflowY: 'auto',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <span
            onClick={onClose}
            data-cy="btn-close"
            style={{
              position: 'absolute',
              marginTop: -16,
              marginRight: -16,
              cursor: 'pointer',
              alignSelf: 'flex-end',
            }}
          >
            <OutlineXIcon />
          </span>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontFamily: 'Nunito',
                fontWeight: 700,
                fontSize: 28,
                color: colors.neutral[600],
                marginBottom: 20,
              }}
            >
              Edit Photo
            </span>
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              cropSize={{ width: 252, height: 252 }}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              style={{
                containerStyle: {
                  position: 'relative',
                  height: 280,
                  width: 420,
                },
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 16,
                marginTop: 32,
              }}
            >
              <Button
                data-cy="btn-cancel"
                variant="outlined"
                size="md"
                onClick={handleLeftButtonClick}
              >
                {leftButtonText}
              </Button>
              <Button
                data-cy="btn-save"
                size="md"
                onClick={handleCrop}
                isLoading={rightButtonLoading}
              >
                Save
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </Portal>
  );
};
